import { ErrorMessage } from '@hookform/error-message';
import { yupResolver } from '@hookform/resolvers/yup';
import { LogoutOutlined } from '@mui/icons-material';
import HttpsOutlinedIcon from '@mui/icons-material/HttpsOutlined';
import {
  Backdrop,
  Box,
  Button,
  CircularProgress,
  IconButton,
  Paper,
  Stack,
  TextField,
  Typography,
} from '@mui/material';
import { EyeIcon } from 'components/SvgIcons/EyeIcon';
import React, { createContext, FC, useContext, useState } from 'react';
import { Controller, useForm } from 'react-hook-form';
import { useMutation } from 'react-query';
import * as yup from 'yup';
import MandatoryFormFieldLabel from '../components/MandatoryFormFieldLabel';
import { useRootContext } from '../components/RootContainer';
import { useFeedback } from '../hooks/useFeedback';
import { useInactivity } from '../hooks/useInactivity';
import { logout } from '../utils/logout';

interface InactivityContextModel {}

const InactivityContext = createContext<InactivityContextModel | undefined>(undefined);

export const Inactivity: FC<{
  children: React.ReactNode;
}> = ({ children }) => {
  const {
    user,
    refetchWhoami,
    ChainApi: {
      settings: {
        acl: {
          user: { setUserInactive },
        },
      },
    },
  } = useRootContext();

  const mutation = useMutation({
    mutationKey: ['setUserInactive'],
    mutationFn: setUserInactive,
    onSuccess: () => {
      refetchWhoami();
    },
  });

  useInactivity({ callBack: () => mutation.mutate() });

  return (
    <InactivityContext.Provider value={{}}>
      <Box
        sx={{
          position: 'relative',
          width: '100%',
          height: '100%',
        }}
      >
        <Backdrop
          sx={{
            color: 'colors.gray.o70',
            zIndex: theme => theme.zIndex.drawer + 1,
            position: 'absolute',
            backgroundColor: 'transparent',
            backdropFilter: 'blur(3px)',
          }}
          open={user && user.user.has_pin && user?.user?.is_inactive}
        >
          <Box>
            <Paper
              elevation={3}
              sx={{
                borderRadius: 8,
                border: '1px solid',
                borderColor: 'colors.gray.o20',
              }}
            >
              <Stack direction={'column'} alignItems={'flex-start'} gap={1} p={3} pb={2}>
                <Stack direction={'row'} alignItems={'center'} gap={1}>
                  <HttpsOutlinedIcon color="warning" />
                  <Typography
                    variant="inter18"
                    fontWeight={'bold'}
                    sx={{ color: 'colors.orange.o40' }}
                  >
                    Session locked
                  </Typography>
                </Stack>
                <Box sx={{ height: 10 }} />
                <Typography variant="body1">Your session has expired due to inactivity.</Typography>
                <Typography variant="body1">To continue, please enter your PIN.</Typography>
                <Box sx={{ height: 10 }} />
                <Box sx={{ pt: 3, width: '100%' }}>
                  <CodePinForm />
                </Box>
              </Stack>
            </Paper>
          </Box>
        </Backdrop>

        {children}
      </Box>
    </InactivityContext.Provider>
  );
};

export const useInactivityCtx = () => {
  const context = useContext(InactivityContext);
  if (!context) {
    throw new Error('useInactivityCtx must be used within a InactivityContext');
  }
  return context;
};

const schema = yup.object().shape({
  codePin: yup.string().required('Code Pin is required'),
});

const CodePinForm: FC = () => {
  const [show, setShow] = useState(false);
  const { onSuccess, onError } = useFeedback();
  const {
    refetchWhoami,
    ChainApi: {
      settings: {
        acl: {
          user: { checkUserPin },
        },
      },
    },
  } = useRootContext();

  const mutation = useMutation({
    mutationKey: ['checkUserPin'],
    mutationFn: checkUserPin,
    onSuccess: data => {
      console.log({ data });
      if (data?.success) {
        refetchWhoami();
        onSuccess({
          snackbar: {
            message: 'PIN verified successfully.',
            title: 'Success',
          },
        });
      } else {
        onError({
          snackbar: {
            message: data?.message
              ? data?.message
              : 'The PIN you entered is incorrect. Please try again',
          },
        });
      }
    },
  });

  const {
    handleSubmit,
    control,
    formState: { errors, isDirty },
  } = useForm<{ codePin: string }>({
    resolver: yupResolver(schema),
    defaultValues: { codePin: '' },
  });

  const onSubmit = handleSubmit(data => {
    mutation.mutate({ pin: data.codePin });
  });

  return (
    <form onSubmit={onSubmit}>
      <Controller
        name="codePin"
        control={control}
        render={({ field }) => (
          <Stack>
            <TextField
              {...field}
              id={`${field.name}-input`}
              label={<MandatoryFormFieldLabel primaryLabel={'code Pin'} />}
              variant="outlined"
              error={!!errors.codePin}
              type={show ? 'text' : 'password'}
              margin="dense"
              autoComplete="off"
              InputProps={{
                endAdornment: (
                  <IconButton onClick={() => setShow(!show)}>
                    <EyeIcon color={show ? 'info' : 'inherit'} />
                  </IconButton>
                ),
              }}
              placeholder={'Insert name'}
            />
            <ErrorMessage
              errors={errors}
              name={field.name}
              render={({ message }) => (
                <Typography variant="inter12" color="error">
                  {message}
                </Typography>
              )}
            />
          </Stack>
        )}
      />

      <Stack direction={'row'} justifyContent={'space-between'} mt={2}>
        <Button
          startIcon={<LogoutOutlined />}
          variant="outlined"
          color="primary"
          type="button"
          size="large"
          sx={{
            width: 'auto',
          }}
          disabled={mutation.isLoading ? true : false}
          id="lock-btn"
          onClick={logout}
        >
          Logout
        </Button>
        <Button
          variant="contained"
          color="primary"
          type="submit"
          size="large"
          sx={{
            width: 'auto',
          }}
          disabled={mutation.isLoading || !isDirty ? true : false}
          id="create-btn"
        >
          {mutation.isLoading && (
            <CircularProgress
              size={20}
              sx={{
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                position: 'absolute',
                width: 'auto',
              }}
            />
          )}
          Send
        </Button>
      </Stack>
    </form>
  );
};
